import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  format,
  getDate,
  getDaysInMonth,
  getMonth,
  getYear,
  isValid
} from 'date-fns';

import SimpleSelect from '../SimpleSelect';
import ValidationMessage from '../ValidationMessage';
import InputWrapper from '../InputWrapper';

import { dateMonths, dateDays, dateYears, generateDays } from './date.utils';
import { deprecateFn } from '../internals/utils';

const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';

export const TEXT_LOCALES = {
  en: {
    ariaDescription: 'Select day, month and year to set a full date',
    placeholders: {
      day: 'Day',
      month: 'Month',
      year: 'Year'
    }
  },
  de: {
    ariaDescription:
      'Wählen Sie Tag, Monat und Jahr, um ein vollständiges Datum festzulegen',
    placeholders: {
      day: 'Tag',
      month: 'Monat',
      year: 'Jahr'
    }
  },
  nl: {
    ariaDescription:
      'Selecteer dag, maand en jaar om een volledige datum in te stellen',
    placeholders: {
      day: 'Dag',
      month: 'Maand',
      year: 'Jaar'
    }
  },
  fr: {
    ariaDescription:
      "Sélectionnez le jour, le mois et l'année pour définir une date complète",
    placeholders: {
      day: 'Journée',
      month: 'Mois',
      year: 'Année'
    }
  },
  sv: {
    ariaDescription:
      'Välj dag, månad och år för att ange ett fullständigt datum',
    placeholders: {
      day: 'Dag',
      month: 'Månad',
      year: 'År'
    }
  },
  es: {
    ariaDescription: 'Seleccione día, mes y año para fijar una fecha completa',
    placeholders: {
      day: 'Día',
      month: 'Mes',
      year: 'Año'
    }
  }
};

class SelectDatePicker extends React.Component {
  defaultYear = getYear(new Date());

  constructor(props) {
    super(props);

    const { noDays } = this.props;

    const state = {
      days: dateDays
    };

    if (props.value === '') {
      state.day = noDays ? '1' : '';
      state.month = '';
      state.year = '';
    } else {
      const nativeDate = new Date(props.value);
      const isDateValid = isValid(nativeDate);

      if (!isDateValid) {
        throw new Error('The value passed is not a valid date');
      } else {
        state.day = getDate(nativeDate);
        state.month = getMonth(nativeDate);
        state.year = getYear(nativeDate);
      }
    }

    this.state = state;
  }

  componentDidMount() {
    const labelPropsDeprecation = deprecateFn();
    labelPropsDeprecation(
      this.props.labelProps,
      'labelProps will be deprecated on version 4.x. Use inputthis.props. instead to pass your label props'
    );
  }

  componentDidUpdate(prevProps) {
    const { noDays } = this.props;
    if (this.props.value !== prevProps.value && this.props.value === '') {
      // the following line is disabled according to guidelines in
      // https://reactjs.org/docs/react-component.html#componentdidupdate
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        day: noDays ? '1' : '',
        month: '',
        year: ''
      });
    }
  }

  dispatchValues = () => {
    const { day, month, year } = this.state;

    if (day && month && year) {
      const nativeDate = new Date(year, month, day);
      const dateISO = format(nativeDate, DEFAULT_DATE_FORMAT);
      this.props.onChange(dateISO);
    }
  };

  handleOnChange = event => {
    const { name, value } = event.target;

    const monthShouldUpdateDays = name === 'month';
    const yearShouldUpdateDays = name === 'year' && !!this.state.month;

    this.setState({ [name]: value }, () => {
      if (monthShouldUpdateDays || yearShouldUpdateDays) {
        this.updateDays(value);
      }

      this.dispatchValues();
    });
  };

  updateDays = () => {
    const { year = this.defaultYear, day, month } = this.state;
    const nextState = {};

    const nativeDate = new Date(year, month);

    const daysInMonth = getDaysInMonth(nativeDate);
    const isActualDayHigherThanDaysInMonth = day > daysInMonth;
    const hasDaySelected = !!day;

    nextState.days = generateDays(daysInMonth);

    if (hasDaySelected && isActualDayHigherThanDaysInMonth) {
      nextState.day = daysInMonth;
    }

    this.setState(nextState);
  };

  render() {
    const {
      isRequired,
      label,
      inputLabelProps,
      labelProps,
      locale,
      maxYear,
      minYear,
      name,
      noDays,
      validationMessage,
      validationProps,
      validationState
    } = this.props;

    const { day, month, year } = this.state;
    const { ariaDescription, placeholders } =
      TEXT_LOCALES[locale] || TEXT_LOCALES.en;

    return (
      <InputWrapper
        role='form'
        aria-roledescription={ariaDescription}
        aria-label={label}
        validationState={validationState}
        label={label}
        isRequired={isRequired}
        aria-labelledby={name}
        inputLabelProps={inputLabelProps || labelProps}
      >
        <div className={classNames('input__row', 'input')}>
          {!noDays && (
            <SimpleSelect
              inputWrapperProps={{
                disableInputStyle: true
              }}
              placeholder={placeholders.day}
              name='day'
              options={this.state.days}
              onChange={this.handleOnChange}
              value={day}
              isSelectGroup
            />
          )}
          <SimpleSelect
            inputWrapperProps={{
              disableInputStyle: true
            }}
            placeholder={placeholders.month}
            name='month'
            options={dateMonths(locale)}
            onChange={this.handleOnChange}
            value={month}
            isSelectGroup
          />
          <SimpleSelect
            inputWrapperProps={{
              disableInputStyle: true
            }}
            placeholder={placeholders.year}
            name='year'
            options={dateYears({ minYear, maxYear })}
            onChange={this.handleOnChange}
            value={year}
            isSelectGroup
          />
        </div>
        <ValidationMessage
          validationState={validationState}
          {...validationProps}
        >
          {validationMessage}
        </ValidationMessage>
      </InputWrapper>
    );
  }
}

SelectDatePicker.defaultProps = {
  isRequired: false,
  locale: 'en',
  noDays: false,
  value: ''
};

SelectDatePicker.propTypes = {
  inputLabelProps: PropTypes.object,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  labelProps: PropTypes.object,
  locale: PropTypes.oneOf(['en', 'fr', 'de', 'nl', 'es', 'sv']),
  maxYear: PropTypes.number,
  minYear: PropTypes.number,
  name: PropTypes.string.isRequired,
  noDays: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  validationMessage: PropTypes.string,
  validationProps: PropTypes.object,
  validationState: PropTypes.oneOf(['success', 'warning', 'error', undefined]),
  value: PropTypes.string
};

export default SelectDatePicker;
